import {
  type BoxProps,
  Button,
  Card,
  Container,
  Flex,
  Image,
  Typography,
} from "@qws-org/ui-kit";
import type { FC, ReactNode } from "react";
import { useRef } from "react";
import { Grid } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, Pages, RowSliderData } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { LazyDirectusImage } from "~/features/row-slider/components/LazyDirectusImage";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type RowSliderProps = {
  data: RowSliderData;
  isControlInactive: (control: Action) => boolean;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
} & BoxProps;

export const RowSlider: FC<RowSliderProps> = ({
  data,
  isControlInactive,
  getUrl,
  onCallAction,
}) => {
  const { icon, action, slides, variant } = data;
  const { getDirectusImage } = useDirectus();

  const mobileImage = icon?.image;
  const mobileWidth = icon?.mobile_image_width ?? undefined;
  const mobileHeight = icon?.mobile_image_height ?? undefined;

  const controlAction = action?.action?.[0];
  const isButtonInactive = action ? isControlInactive(action) : true;

  const isDraggingRef = useRef(false);

  let href;

  if (isExternalLink(controlAction?.item)) {
    if (isTemplateUrl(controlAction?.item)) {
      href = undefined;
    } else {
      href = controlAction.item.href ?? undefined;
    }
  }
  if (isInternalLink(controlAction?.item)) {
    href = controlAction.item.page
      ? getUrl(controlAction.item.page)
      : undefined;
  }
  const handlePressButton = (): void => {
    if (action && !isButtonInactive) {
      onCallAction(action);
    }
  };

  const handleTouchMove = (): void => {
    isDraggingRef.current = true;
  };

  const handleTouchEnd = (): void => {
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  };

  const renderSlides = (): ReactNode =>
    slides?.map((item, index) => {
      const slideImage = item?.row_slider_slides_id?.image ?? undefined;
      const mWidth = slideImage?.mobile_image_width ?? undefined;
      const mHeight = slideImage?.mobile_image_height ?? undefined;
      const mdWidth = slideImage?.tablet_image_width ?? mWidth;
      const mdHeight = slideImage?.tablet_image_height ?? mHeight;
      const dWidth = slideImage?.desktop_image_width ?? mdWidth;
      const dHeight = slideImage?.desktop_image_height ?? mdHeight;

      const slideAction = item?.row_slider_slides_id?.action;
      const slideControlAction = slideAction?.action?.[0];
      const isSlideButtonInactive = slideAction
        ? isControlInactive(slideAction)
        : true;

      let slideHref: string | undefined;

      if (isExternalLink(slideControlAction?.item)) {
        if (isTemplateUrl(slideControlAction?.item)) {
          slideHref = undefined;
        } else {
          slideHref = slideControlAction.item.href ?? undefined;
        }
      }

      if (isInternalLink(slideControlAction?.item)) {
        slideHref = slideControlAction.item.page
          ? getUrl(slideControlAction.item.page)
          : undefined;
      }

      const handlePressSlideButton = (): void => {
        if (isDraggingRef.current) {
          isDraggingRef.current = false;
          return;
        }
        if (slideAction && !isSlideButtonInactive) {
          onCallAction(slideAction);
        }
      };
      if (!mWidth || !mHeight) {
        return null;
      }

      return (
        <SwiperSlide
          key={index}
          style={{
            width: "auto",
            height: "auto",
          }}
        >
          <Button
            href={slideHref}
            onPress={handlePressSlideButton}
            variant="tertiary"
          >
            <Card
              border={{ radius: "card", style: "none" }}
              overflow={"hidden"}
              variant={"default"}
              width={mWidth}
              height={mHeight}
              md={{ width: mdWidth, height: mdHeight }}
              d={{ width: dWidth, height: dHeight }}
            >
              {slideImage?.image?.id && (
                <LazyDirectusImage picture={slideImage} format="webp" />
              )}
            </Card>
          </Button>
        </SwiperSlide>
      );
    });

  return (
    <Container p={{ px: 0, py: 0 }} m={{ mx: "auto" }} flexDirection={"column"}>
      <Flex
        width="100%"
        p={{ px: 16, py: 12 }}
        md={{
          p: { px: 24 },
        }}
        d={{
          p: { px: 32 },
        }}
        display={"flex"}
        justify="space-between"
        gap={8}
      >
        <Flex align="center" gap={8}>
          {mobileImage?.id && (
            <Image
              value={getDirectusImage(mobileImage.id, {
                width: mobileWidth,
                height: mobileHeight,
                format: "webp",
              })}
              overflow="hidden"
              image={{
                className: "object-cover object-center",
                alt: icon?.alternative_text ?? "",
              }}
              width={mobileWidth}
              height={mobileHeight}
              minWidth="fit-content"
            />
          )}

          {data?.title && (
            <Typography
              size={"medium"}
              md={{
                fontSize: "title.desktop.small",
              }}
              d={{
                fontSize: "title.desktop.small",
              }}
              fontWeight={"mobile.bold"}
            >
              {data.title}
            </Typography>
          )}
        </Flex>
        {data?.action_text && (
          <Button
            p={{ px: 12, py: 8 }}
            variant="tertiary"
            border={{
              width: "1",
              color: "border-subtle",
            }}
            size="xsmall"
            fontWeight="mobile.bold"
            href={!isButtonInactive ? href : undefined}
            onPress={handlePressButton}
          >
            {data?.action_text}
          </Button>
        )}
      </Flex>

      <Flex justify={slides?.length === 1 ? "center" : "normal"}>
        <Swiper
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          grabCursor
          modules={variant === "grid" ? [Grid] : []}
          slidesPerView="auto"
          grid={variant === "grid" ? { rows: 2, fill: "row" } : undefined}
          className="!m-0"
          style={{ paddingRight: "24px" }}
          slidesOffsetBefore={16}
          breakpoints={{
            768: {
              slidesOffsetBefore: 24,
            },
            1024: {
              slidesOffsetBefore: 32,
            },
          }}
          spaceBetween={8}
        >
          {renderSlides()}
        </Swiper>
      </Flex>
    </Container>
  );
};
